import { hasModifierKey } from '@angular/cdk/keycodes';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  booleanAttribute,
  computed,
  Directive,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  InjectionToken,
  Input,
  Output,
  signal,
  untracked,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { merge, startWith, Subscription, switchMap } from 'rxjs';

import { AutocompleteOptionDirective } from './autocomplete-option.directive';
import { AutocompleteOriginDirective } from './autocomplete-origin.directive';
import { AutocompleteComponent } from './autocomplete.component';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';

export const TT_FORM_FIELD = new InjectionToken<FormFieldControl>('FormFieldControl');

@Directive({
  selector: 'input[ttAutocomplete], textarea[ttAutocomplete]',
  standalone: true,
})
export class AutocompleteTriggerDirective<T> implements ControlValueAccessor, FormFieldControl {
  private _element = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private _viewContainerRef = inject(ViewContainerRef);
  private _overlay = inject(Overlay);
  public ngControl = inject(NgControl, { optional: true, self: true });
  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentForm = inject(NgForm, { optional: true });
  private parentFormGroup = inject(FormGroupDirective, { optional: true });
  private _document = inject(DOCUMENT);

  private _overlayAttached = false;
  private _previousValue: string | number | null;
  private _overlayRef: OverlayRef | null;
  private _portal: TemplatePortal;
  private errorStateTracker: ErrorStateTracker;
  private _positionStrategy: FlexibleConnectedPositionStrategy | null = null;
  private _mutationObserver: MutationObserver | null = null;

  errorState = computed(() => this.errorStateTracker.errorState());

  @Input('ttAutocomplete') autocomplete: AutocompleteComponent<T>;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('ttAutocompleteConnectedTo') connectedTo: AutocompleteOriginDirective;

  /**
   * Whether the autocomplete is disabled. When disabled, the element will
   * act as a regular input and the user won't be able to open the panel.
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input({ alias: 'ttAutocompleteDisabled', transform: booleanAttribute })
  autocompleteDisabled: boolean;

  @Input() public labelKey: keyof T;

  @Output() optionSelected = new EventEmitter<T>();

  private readonly _error = signal(false);
  @Input()
  set error(value: boolean) {
    this._error.set(value);
  }

  private _optionSelectionSub: Subscription | null = null;

  constructor() {
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);

    effect(() => {
      if (this.ngControl) {
        const errorState = this.errorStateTracker.errorState();
        untracked(() => this._error.set(errorState));
      }
    });
  }

  get activeOption(): AutocompleteOptionDirective<T> | null {
    return this.autocomplete?.keyManager?.activeItem ?? null;
  }

  get isPanelOpen(): boolean {
    return this._overlayAttached && this.autocomplete.showPanel();
  }

  @HostListener('input', ['$event'])
  protected handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let value: number | string | null = target.value;

    if (target.type === 'number') {
      value = value == '' ? null : parseFloat(value);
    }

    if (this._previousValue !== value) {
      this._previousValue = value;

      if (!this.autocomplete || !this.autocomplete.requireSelection) {
        this._onChange(value);
      }

      if (!value) {
        this._updateInputValue(null);
      } else if (this.isPanelOpen && !this.autocomplete.requireSelection) {
        const selectedOption = this.autocomplete.options?.find((option) => option.isSelected);

        if (selectedOption && value !== selectedOption.value) {
          selectedOption.deselect();
        }
      }

      if (this.canOpen() && this._document.activeElement === event.target) {
        this._attachOverlay();
      }
    }
  }

  @HostListener('click')
  protected handleClick(): void {
    if (this.canOpen()) {
      this._attachOverlay();
    }
  }

  @HostListener('focusin')
  protected handleFocus(): void {
    if (this.canOpen()) {
      this._attachOverlay();
    }
  }

  @HostListener('blur')
  protected handleBlur(): void {
    this._onTouched();

    setTimeout(() => {
      this.closePanel();
      this.checkValue();
    }, 100);
  }

  private checkValue(): void {
    const currentFormValue = this.ngControl?.control.value;
    const value = this._element.nativeElement.value;

    if (this.autocomplete && this.autocomplete.options && this.autocomplete.requireSelection) {
      const matchingOption = this.autocomplete.options.find((option) => option.value === value);
      if (!matchingOption) {
        this._updateInputValue(currentFormValue);
      }
    }
  }

  private canOpen(): boolean {
    const element = this._element.nativeElement;
    return !element.readOnly && !element.disabled && !this.autocompleteDisabled;
  }

  @HostListener('keydown', ['$event'])
  public handleKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const hasModifier = hasModifierKey(event);

    if (key === 'Enter' && this.activeOption && !hasModifier) {
      this._updateInputValue(this.activeOption.value);
      this.optionSelected.emit(this.activeOption.value);
      this.autocomplete.keyManager.activeItem.selected.emit();
      this.closePanel();
    } else if (this.autocomplete) {
      this.autocomplete.keyManager.onKeydown(event);
    }
  }

  private _updateInputValue(value: T): void {
    const valueLabel = value ? (this.labelKey ? value[this.labelKey].toString() : value.toString()) : '';

    if (this.ngControl?.control) {
      this.ngControl.control.setValue(valueLabel);
    } else {
      this._element.nativeElement.value = valueLabel;
    }
  }

  private _attachOverlay(): void {
    if (!this.autocomplete) {
      throw new Error('AutocompleteTriggerDirective: missing autocomplete component');
    }

    let overlayRef = this._overlayRef;
    if (!overlayRef) {
      this._portal = new TemplatePortal(this.autocomplete.template, this._viewContainerRef);
      overlayRef = this._overlay.create(this._getOverlayConfig());
      this._overlayRef = overlayRef;
    } else {
      overlayRef.updateSize({ width: this._getPanelWidth() });
    }

    this.autocomplete.setVisibility();

    if (overlayRef && !overlayRef.hasAttached()) {
      this._overlayAttached = true;
      overlayRef.attach(this._portal);
      this._startPositionObserver();

      this._optionSelectionSub?.unsubscribe();

      this._optionSelectionSub = this.autocomplete.options.changes
        .pipe(
          startWith(this.autocomplete.options),
          switchMap(() => merge(...this.autocomplete.options.map((option) => option?.selected))),
        )
        .subscribe((dir) => {
          this.autocomplete.setVisibility();

          if (dir) {
            this.setValueAndClose(dir.source.value);
          }
        });
    }
  }

  private _getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      positionStrategy: this._getOverlayPosition(),
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      width: this._getPanelWidth(),
      panelClass: 'tt-autocomplete_panel',
    });
  }

  private _getOverlayPosition(): PositionStrategy {
    this._positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._getConnectedElement())
      .withFlexibleDimensions(false)
      .withPush(false);

    this._setStrategyPositions(this._positionStrategy);
    return this._positionStrategy;
  }

  private _setStrategyPositions(positionStrategy: FlexibleConnectedPositionStrategy) {
    positionStrategy.withPositions([
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    ]);
  }

  private _getConnectedElement(): ElementRef<HTMLElement> {
    if (this.connectedTo) {
      return this.connectedTo.elementRef;
    }

    return this._element;
  }

  private _getPanelWidth(): number | string {
    return this._getConnectedElement().nativeElement.getBoundingClientRect().width;
  }

  private setValueAndClose(value?: T): void {
    const panel = this.autocomplete;

    if (value) {
      this._updateInputValue(value);
      this.optionSelected.emit(value);
    } else if (panel.requireSelection) {
      this._updateInputValue(null);
    }

    this.closePanel();
  }

  closePanel(): void {
    if (!this._overlayAttached) {
      return;
    }
    this._overlayAttached = false;
    this._stopPositionObserver();
    this._overlayRef?.detach();
  }

  private _startPositionObserver(): void {
    this._stopPositionObserver();
    const connectedEl = this._getConnectedElement().nativeElement;
    // Observe the parent for child mutations — any tag added/removed shifts the input,
    // and CDK's FlexibleConnectedPositionStrategy.reapplyLastPosition() repositions
    // the overlay using the already-known anchor element without a full recalculation.
    const observeTarget = connectedEl.parentElement ?? connectedEl;
    this._mutationObserver = new MutationObserver(() => {
      this._positionStrategy?.reapplyLastPosition();
      if (this._overlayRef) {
        this._overlayRef.updateSize({ width: this._getPanelWidth() });
      }
    });
    this._mutationObserver.observe(observeTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  private _stopPositionObserver(): void {
    this._mutationObserver?.disconnect();
    this._mutationObserver = null;
  }

  // ------------- ControlValueAccessor related methods -------------

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange: (value: unknown) => void = () => {};

  public writeValue(value: string): void {
    this._element.nativeElement.value = value;
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this._element.nativeElement.disabled = isDisabled;
  }
}
